import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9088/api';

const StripeSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // 'success', 'error', or ''
  const [settings, setSettings] = useState({
    stripe_secret_key: '',
    stripe_publishable_key: '',
    stripe_webhook_secret: '',
    stripe_mode: 'test', // test or live
  });
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/admin/stripe-settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setSettings(response.data.data || {
          stripe_secret_key: '',
          stripe_publishable_key: '',
          stripe_webhook_secret: '',
          stripe_mode: 'test',
        });
      }
    } catch (error) {
      console.error('Failed to fetch Stripe settings:', error);
      setErrorMessage('Failed to load Stripe settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveStatus('');
      setSuccessMessage('');
      setErrorMessage('');

      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_URL}/admin/stripe-settings`,
        settings,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setSuccessMessage('Stripe settings saved successfully!');
        setSaveStatus('success');
        setTimeout(() => {
          setSuccessMessage('');
          setSaveStatus('');
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to save Stripe settings:', error);
      setErrorMessage(error.response?.data?.error?.message || 'Failed to save settings');
      setSaveStatus('error');
      setTimeout(() => {
        setErrorMessage('');
        setSaveStatus('');
      }, 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const maskKey = (key) => {
    if (!key) return '';
    if (key.length <= 12) return '•'.repeat(key.length);
    return key.substring(0, 7) + '•'.repeat(key.length - 11) + key.substring(key.length - 4);
  };

  const getKeyStatus = (key, expectedPrefix) => {
    if (!key) return { status: 'empty', message: 'Not configured' };
    if (!key.startsWith(expectedPrefix)) {
      return { status: 'warning', message: `Should start with ${expectedPrefix}` };
    }
    return { status: 'success', message: 'Configured' };
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const secretKeyStatus = getKeyStatus(
    settings.stripe_secret_key,
    settings.stripe_mode === 'test' ? 'sk_test_' : 'sk_live_'
  );
  const publishableKeyStatus = getKeyStatus(
    settings.stripe_publishable_key,
    settings.stripe_mode === 'test' ? 'pk_test_' : 'pk_live_'
  );
  const webhookSecretStatus = getKeyStatus(settings.stripe_webhook_secret, 'whsec_');

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Stripe Settings</h1>
              <p className="mt-2 text-gray-600">
                Configure your Stripe payment gateway settings
              </p>
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {successMessage}
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {errorMessage}
                </div>
              </div>
            )}

            {/* Mode Selection */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Stripe Mode</h2>
              <div className="flex space-x-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="stripe_mode"
                    value="test"
                    checked={settings.stripe_mode === 'test'}
                    onChange={(e) => handleInputChange('stripe_mode', e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-gray-700">
                    Test Mode
                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      Development
                    </span>
                  </span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="stripe_mode"
                    value="live"
                    checked={settings.stripe_mode === 'live'}
                    onChange={(e) => handleInputChange('stripe_mode', e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-gray-700">
                    Live Mode
                    <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                      Production
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* API Keys */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">API Keys</h2>
              <p className="text-sm text-gray-600 mb-6">
                Get your API keys from{' '}
                <a
                  href="https://dashboard.stripe.com/apikeys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Stripe Dashboard → Developers → API keys
                </a>
              </p>

              <div className="space-y-6">
                {/* Secret Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secret Key
                    <span className="ml-2 text-xs text-gray-500">
                      (sk_{settings.stripe_mode}_...)
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type={showSecretKey ? 'text' : 'password'}
                      value={settings.stripe_secret_key}
                      onChange={(e) => handleInputChange('stripe_secret_key', e.target.value)}
                      placeholder={`sk_${settings.stripe_mode}_...`}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecretKey(!showSecretKey)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showSecretKey ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="mt-2 flex items-center text-sm">
                    <span
                      className={`flex items-center ${
                        secretKeyStatus.status === 'success'
                          ? 'text-green-600'
                          : secretKeyStatus.status === 'warning'
                          ? 'text-yellow-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {secretKeyStatus.status === 'success' && (
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {secretKeyStatus.message}
                    </span>
                  </div>
                </div>

                {/* Publishable Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publishable Key
                    <span className="ml-2 text-xs text-gray-500">
                      (pk_{settings.stripe_mode}_...)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={settings.stripe_publishable_key}
                    onChange={(e) => handleInputChange('stripe_publishable_key', e.target.value)}
                    placeholder={`pk_${settings.stripe_mode}_...`}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="mt-2 flex items-center text-sm">
                    <span
                      className={`flex items-center ${
                        publishableKeyStatus.status === 'success'
                          ? 'text-green-600'
                          : publishableKeyStatus.status === 'warning'
                          ? 'text-yellow-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {publishableKeyStatus.status === 'success' && (
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {publishableKeyStatus.message}
                    </span>
                  </div>
                </div>

                {/* Webhook Secret */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webhook Signing Secret
                    <span className="ml-2 text-xs text-gray-500">(whsec_...)</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showWebhookSecret ? 'text' : 'password'}
                      value={settings.stripe_webhook_secret}
                      onChange={(e) => handleInputChange('stripe_webhook_secret', e.target.value)}
                      placeholder="whsec_..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showWebhookSecret ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="mt-2 flex items-center text-sm">
                    <span
                      className={`flex items-center ${
                        webhookSecretStatus.status === 'success'
                          ? 'text-green-600'
                          : webhookSecretStatus.status === 'warning'
                          ? 'text-yellow-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {webhookSecretStatus.status === 'success' && (
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {webhookSecretStatus.message}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-600">
                    Get this from Stripe Dashboard → Developers → Webhooks → Your endpoint
                  </p>
                </div>
              </div>
            </div>

            {/* Webhook Endpoint Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Webhook Endpoint</h3>
              <p className="text-sm text-blue-800 mb-3">
                Configure this URL in your Stripe Dashboard:
              </p>
              <div className="bg-white rounded px-4 py-2 font-mono text-sm text-gray-800 border border-blue-300">
                https://api.getaizest.com/api/payment/webhook
              </div>
              <p className="mt-3 text-xs text-blue-700">
                Make sure to select the following events: checkout.session.completed, customer.subscription.*,
                invoice.payment_succeeded, invoice.payment_failed
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={fetchSettings}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saving || saveStatus === 'success'}
                className={`px-6 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 ${
                  saveStatus === 'success'
                    ? 'bg-green-600 text-white'
                    : saveStatus === 'error'
                    ? 'bg-red-600 text-white'
                    : saving
                    ? 'bg-blue-600 text-white opacity-50 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {saveStatus === 'success' && (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                {saveStatus === 'error' && (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                {saving && (
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>
                  {saving
                    ? 'Saving...'
                    : saveStatus === 'success'
                    ? 'Saved Successfully!'
                    : saveStatus === 'error'
                    ? 'Save Failed'
                    : 'Save Settings'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeSettings;
